import {
    Box,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    TableCell,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {useState} from "react";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import {deleteCoupon, toggleCouponStatus} from "../../services/system/CouponService";
import CouponDialog from "./CouponDialog";
import {parseMediaDate} from "../../utils/progressMediaDate";

const CouponCard = ({coupon, reload}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [editDialog, setEditDialog] = useState(false);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        setEditDialog(true);
        handleClose();
    };

    const handleDelete = async () => {
        try {
            await deleteCoupon(coupon.id);
            reload();
            handleClose();
        } catch (error) {
            console.error("Error deleting coupon:", error);
        }
    };

    const handleToggleStatus = async () => {
        try {
            await toggleCouponStatus(coupon.id, !coupon.isActive);
            reload();
            handleClose();
        } catch (error) {
            console.error("Error toggling coupon status:", error);
        }
    };

    // validUntil is a LocalDate (a CALENDAR DAY), so it must not be routed through a timezone.
    // `new Date("2026-08-31")` parses as UTC midnight and toLocaleDateString renders it in the
    // viewer's zone, showing 30/08 west of UTC. CouponDialog was already fixed for this; the card
    // that displays the result was not. parseMediaDate also covers the Jackson [y,m,d] shape.
    const formatDate = (dateString) => {
        const date = parseMediaDate(dateString);
        return date ? date.toLocaleDateString('he-IL') : '';
    };

    const getDiscountText = () => {
        if (coupon.discountPercentage === 100) {
            return "חינם";
        }
        return `${coupon.discountPercentage}% הנחה`;
    };

    const getDurationText = (duration) => {
        const durationMap = {
            'DAY': 'ליום',
            'WEEK': 'לשבוע',
            'MONTH': 'לחודש',
            'YEAR': 'לשנה',
            'LIFETIME': 'לכל החיים'
        };
        return durationMap[duration] || duration;
    };

    const getStatusChip = () => {
        if (!coupon.isActive) {
            return <Chip label="לא פעיל" color="error" size="small" sx={{ fontSize: "11px", height: "20px" }} />;
        }
        if (coupon.isExpired) {
            return <Chip label="פג תוקף" color="warning" size="small" sx={{ fontSize: "11px", height: "20px" }} />;
        }
        if (coupon.isFullyUsed) {
            return <Chip label="מוצה" color="secondary" size="small" sx={{ fontSize: "11px", height: "20px" }} />;
        }
        return <Chip label="פעיל" color="success" size="small" sx={{ fontSize: "11px", height: "20px" }} />;
    };

    const getUsageText = () => {
        return `${coupon.currentUsageCount}/${coupon.maxUsageCount}`;
    };

    // Mobile Card Layout
    if (isMobile) {
        return (
            <>
                <CouponDialog
                    isOpen={editDialog}
                    close={() => setEditDialog(false)}
                    setReload={reload}
                    editCoupon={coupon}
                />

                <Box
                    onClick={handleEdit}
                    sx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        padding: "12px 14px",
                        marginBottom: "8px",
                        cursor: "pointer",
                        direction: "rtl",
                        "&:hover": {
                            backgroundColor: "#F8FAFC",
                            borderColor: "#CBD5E1"
                        },
                        "&:active": {
                            backgroundColor: "#F1F5F9"
                        }
                    }}
                >
                    {/* Top Row: Code + Status + Actions */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "6px"
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: "#1E293B",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {coupon.code}
                            </Typography>
                            {getStatusChip()}
                        </Box>
                        <IconButton
                            onClick={handleClick}
                            size="small"
                            sx={{ color: "#6B7280", padding: "4px", marginRight: 1 }}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Company Name */}
                    {coupon.companyName && (
                        <Typography
                            sx={{
                                fontSize: "12px",
                                color: "#64748B",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                marginBottom: "10px"
                            }}
                        >
                            {coupon.companyName}
                        </Typography>
                    )}

                    {/* Info Row */}
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: "#F8FAFC",
                        borderRadius: "6px",
                        padding: "8px 10px"
                    }}>
                        {/* Discount */}
                        <Box sx={{ textAlign: "center", flex: 1 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#059669" }}>
                                {getDiscountText()}
                            </Typography>
                            <Typography sx={{ fontSize: "10px", color: "#94A3B8" }}>
                                הנחה
                            </Typography>
                        </Box>

                        <Box sx={{ width: "1px", height: "24px", backgroundColor: "#E2E8F0" }} />

                        {/* Duration */}
                        <Box sx={{ textAlign: "center", flex: 1 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1E293B" }}>
                                {coupon.duration ? getDurationText(coupon.duration) : '-'}
                            </Typography>
                            <Typography sx={{ fontSize: "10px", color: "#94A3B8" }}>
                                תקופה
                            </Typography>
                        </Box>

                        <Box sx={{ width: "1px", height: "24px", backgroundColor: "#E2E8F0" }} />

                        {/* Usage */}
                        <Box sx={{ textAlign: "center", flex: 1 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1E293B" }}>
                                {getUsageText()}
                            </Typography>
                            <Typography sx={{ fontSize: "10px", color: "#94A3B8" }}>
                                שימוש
                            </Typography>
                        </Box>

                        <Box sx={{ width: "1px", height: "24px", backgroundColor: "#E2E8F0" }} />

                        {/* Expiry */}
                        <Box sx={{ textAlign: "center", flex: 1 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: coupon.isExpired ? "#DC2626" : "#1E293B" }}>
                                {formatDate(coupon.validUntil)}
                            </Typography>
                            <Typography sx={{ fontSize: "10px", color: "#94A3B8" }}>
                                תוקף
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Menu for actions */}
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        'aria-labelledby': 'basic-button',
                    }}
                >
                    <MenuItem onClick={handleEdit}>
                        <EditIcon sx={{marginLeft: "8px", fontSize: "18px", color: "#6B7280"}} />
                        <Typography sx={{ fontSize: "14px" }}>עריכה</Typography>
                    </MenuItem>

                    <MenuItem onClick={handleToggleStatus}>
                        {coupon.isActive ? (
                            <>
                                <PauseIcon sx={{marginLeft: "8px", fontSize: "18px", color: "#6B7280"}} />
                                <Typography sx={{ fontSize: "14px" }}>השבת</Typography>
                            </>
                        ) : (
                            <>
                                <PlayArrowIcon sx={{marginLeft: "8px", fontSize: "18px", color: "#6B7280"}} />
                                <Typography sx={{ fontSize: "14px" }}>הפעל</Typography>
                            </>
                        )}
                    </MenuItem>

                    {coupon.currentUsageCount === 0 && (
                        <MenuItem onClick={handleDelete} sx={{color: "error.main"}}>
                            <DeleteIcon sx={{marginLeft: "8px", fontSize: "18px"}} />
                            <Typography sx={{ fontSize: "14px" }}>מחק</Typography>
                        </MenuItem>
                    )}
                </Menu>
            </>
        );
    }

    // Desktop Table Row Layout
    return (
        <>
            <CouponDialog
                isOpen={editDialog}
                close={() => setEditDialog(false)}
                setReload={reload}
                editCoupon={coupon}
            />

            <TableRow sx={{
                "&.MuiTableRow-root": {
                    border: "1px solid #E7E9EE",
                    height: "60px", // Reduced from default to match template
                    backgroundColor: "#F9FAFB",
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor: "rgba(185,217,213,0.41)"
                    },
                    boxShadow: "0px 0px 1px rgba(12, 26, 75, 0.1), 0px 4px 8px rgba(20, 37, 63, 0.06)" // Reduced shadow to match template
                }
            }}>
                <TableCell align="right" sx={{ padding: "8px 12px" }}>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "20px",
                            color: "#4F535C",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {coupon.code}
                    </Typography>
                </TableCell>

                <TableCell align="right" sx={{ padding: "8px 4px" }}>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            lineHeight: "18px",
                            color: "#6B7280",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {coupon.companyName}
                    </Typography>
                </TableCell>

                <TableCell align="right" sx={{ padding: "8px 4px" }}>
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "4px", maxWidth: "100%"}}>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                lineHeight: "18px",
                                color: "#6B7280",
                                whiteSpace: "nowrap"
                            }}
                        >
                            {getDiscountText()}
                        </Typography>
                        {coupon.isFree && (
                            <Chip
                                label="חינם"
                                size="small"
                                sx={{
                                    backgroundColor: "#10B981",
                                    color: "white",
                                    fontSize: "10px",
                                    height: "18px",
                                    minWidth: "auto"
                                }}
                            />
                        )}
                    </Box>
                </TableCell>

                <TableCell align="right" sx={{ padding: "8px 4px" }}>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            lineHeight: "18px",
                            color: "#6B7280"
                        }}
                    >
                        {coupon.duration ? getDurationText(coupon.duration) : '-'}
                    </Typography>
                </TableCell>

                <TableCell align="right" sx={{ padding: "8px 4px" }}>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            lineHeight: "18px",
                            color: "#6B7280"
                        }}
                    >
                        {formatDate(coupon.validUntil)}
                    </Typography>
                </TableCell>

                <TableCell align="right" sx={{ padding: "8px 4px" }}>
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            lineHeight: "18px",
                            color: "#6B7280"
                        }}
                    >
                        {getUsageText()}
                    </Typography>
                </TableCell>

                <TableCell align="right" sx={{ padding: "8px 4px" }}>
                    {getStatusChip()}
                </TableCell>

                <TableCell align="left" sx={{ padding: "8px 12px" }}>
                    <IconButton
                        onClick={handleClick}
                        size="small"
                        sx={{ color: "#6B7280" }}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                            'aria-labelledby': 'basic-button',
                        }}
                    >
                        <MenuItem onClick={handleEdit}>
                            <EditIcon sx={{marginLeft: "8px", fontSize: "18px"}} />
                            <Typography sx={{ fontSize: "14px" }}>עריכה</Typography>
                        </MenuItem>

                        <MenuItem onClick={handleToggleStatus}>
                            {coupon.isActive ? (
                                <>
                                    <PauseIcon sx={{marginLeft: "8px", fontSize: "18px"}} />
                                    <Typography sx={{ fontSize: "14px" }}>השבת</Typography>
                                </>
                            ) : (
                                <>
                                    <PlayArrowIcon sx={{marginLeft: "8px", fontSize: "18px"}} />
                                    <Typography sx={{ fontSize: "14px" }}>הפעל</Typography>
                                </>
                            )}
                        </MenuItem>

                        {coupon.currentUsageCount === 0 && (
                            <MenuItem onClick={handleDelete} sx={{color: "error.main"}}>
                                <DeleteIcon sx={{marginLeft: "8px", fontSize: "18px"}} />
                                <Typography sx={{ fontSize: "14px" }}>מחק</Typography>
                            </MenuItem>
                        )}
                    </Menu>
                </TableCell>
            </TableRow>
        </>
    );
};

export default CouponCard;